import { IconRalphWiggum } from "@/assets/icons/IconRalphWiggum";
import { Markdown } from "@/components/ai/chat/Markdown";
import { AgentMode } from "@/components/ai/prompt-input/agent-mode";
import { agentModeAtomFamily } from "@/components/ai/chat/state";
import { useProgressiveText } from "@/hooks/use-progressive-text";
import type { AgentMessageFragmentText } from "@/hooks/use-stream-conversation";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import { agentExecutionLoading, agentExecutionEnvironmentIds } from "@/utils/agent-executions";
import { cn } from "@/components/podkit/lib/cn";
import { useSetAtom } from "jotai";
import { type FC, useEffect } from "react";

const RALPH_COMPLETE_MARKER = "RALPH_COMPLETE";
const RALPH_SPEC_COMPLETE_MARKER = "RALPH_SPEC_COMPLETE";
const RALPH_ITERATION_COMPLETE_PREFIX = "RALPH_ITERATION_COMPLETE(";

/**
 * Checks if a marker appears on its own line in the content.
 * This prevents false positives from markers appearing in discussion text.
 * Matches the backend's containsMarkerOnOwnLine behavior.
 */
function containsMarkerOnOwnLine(content: string, marker: string): boolean {
    const lines = content.split("\n");
    return lines.some((line) => line.trim() === marker);
}

const RalphStatusMessage: FC<{ message: string }> = ({ message }) => (
    <div className="mt-2 flex items-center gap-2 text-sm text-content-secondary">
        <IconRalphWiggum size={20} />
        <span>{message}</span>
    </div>
);

/**
 * Parses RALPH_ITERATION_COMPLETE(current/max) marker and returns iteration info.
 * Returns null if not found.
 */
function parseIterationMarker(content: string): { current: number; max: number } | null {
    const startIdx = content.indexOf(RALPH_ITERATION_COMPLETE_PREFIX);
    if (startIdx === -1) return null;

    const endIdx = content.indexOf(")", startIdx);
    if (endIdx === -1) return null;

    const inner = content.slice(startIdx + RALPH_ITERATION_COMPLETE_PREFIX.length, endIdx);
    const parts = inner.split("/");
    if (parts.length !== 2) return null;

    const current = parseInt(parts[0], 10);
    const max = parseInt(parts[1], 10);
    if (isNaN(current) || isNaN(max)) return null;

    return { current, max };
}

export const FragmentText: FC<{
    fragment: AgentMessageFragmentText;
    agentExecution?: PlainAgentExecution;
    shouldHighlight?: boolean;
}> = ({ fragment, agentExecution, shouldHighlight = false }) => {
    const isAgentRunning = agentExecutionLoading(agentExecution);
    const isCompleted = fragment.completed || !isAgentRunning;
    const { text } = useProgressiveText(fragment.content, { speed: 10, enabled: !isCompleted });

    // Get environment ID to reset agent mode when ralph completes
    const environmentIds = agentExecutionEnvironmentIds(agentExecution);
    const environmentId = environmentIds.length > 0 ? environmentIds[0] : "";
    const setAgentMode = useSetAtom(agentModeAtomFamily(environmentId));

    // Remove summary heading if present (case-insensitive)
    const contentToDisplay = isCompleted ? fragment.content : text;
    const processedContent = shouldHighlight ? contentToDisplay.replace(/^#+\s*summary\s*\n*/i, "") : contentToDisplay;

    // Check if content contains Ralph markers (must be on their own line to avoid false positives)
    const hasRalphComplete = containsMarkerOnOwnLine(processedContent, RALPH_COMPLETE_MARKER);
    const hasRalphSpecComplete = containsMarkerOnOwnLine(processedContent, RALPH_SPEC_COMPLETE_MARKER);
    const iterationInfo = parseIterationMarker(processedContent);

    // Reset agent mode to Agent when ralph completes
    useEffect(() => {
        if (hasRalphComplete && environmentId) {
            setAgentMode(AgentMode.Agent);
        }
    }, [hasRalphComplete, environmentId, setAgentMode]);

    let finalContent = processedContent;
    if (hasRalphSpecComplete) {
        finalContent = finalContent.replace(RALPH_SPEC_COMPLETE_MARKER, "").trim();
    }
    if (hasRalphComplete) {
        finalContent = finalContent.replace(RALPH_COMPLETE_MARKER, "").trim();
    }
    if (iterationInfo) {
        // Remove the iteration marker from displayed content
        const markerRegex = /RALPH_ITERATION_COMPLETE\(\d+\/\d+\)/g;
        finalContent = finalContent.replace(markerRegex, "").trim();
    }

    return (
        <div>
            {shouldHighlight && <div className="mb-2 text-xs font-medium text-content-muted">Summary</div>}
            <div
                data-completed={isCompleted}
                className={cn({
                    "rounded-lg border border-border-base bg-surface-secondary p-4": shouldHighlight,
                })}
            >
                {finalContent && <Markdown content={finalContent} />}
                {hasRalphSpecComplete && (
                    <RalphStatusMessage message="My brain is done talking, so my keyboard gets a turn." />
                )}
                {hasRalphComplete && <RalphStatusMessage message="is done" />}
                {iterationInfo && (
                    <RalphStatusMessage
                        message={`Iteration ${iterationInfo.current}/${iterationInfo.max} complete, continuing...`}
                    />
                )}
            </div>
        </div>
    );
};
